'use client'

import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { usePathname, useRouter } from 'next/navigation'
import { useLocale } from 'next-intl'
import * as Sentry from '@sentry/nextjs'
import { initAuth, type AuthState } from '@/lib/auth'
import { recordPromoAttribution, verifySupabaseUrlMatch } from '@/lib/api'
import { supabase } from '@/lib/supabase'
import { identifyUser, resetUser, linkPreviousAnonymousIdentity, clearPreResetIdentity } from '@/lib/analytics'
import { captureSentryError } from '@/lib/sentry'
import { isAppRoute, stripLocalePrefix } from '@/i18n/surfaces'
import { persistLocaleChoice, readLocaleCookie } from '@/lib/locale'
import { AUTH_SESSION_INVALID_EVENT } from '@/lib/authSessionEvents'
import { syncLibraryAccessEmail } from '@/lib/libraryFlag'
import { shouldRefreshAuthState } from '@/lib/authLifecycle'
import { setLibraryTransportAccount } from '@/store/libraryTransport'
import { captureReferralCode, getStoredPromoSlug } from '@/lib/signupAccessStorage'
import { useAuthCaptcha } from '@/components/auth/useAuthCaptcha'

export type AuthErrorKind = 'config' | 'network'

interface AuthContextValue {
  auth: AuthState | null
  isLoading: boolean
  error: Error | null
  errorKind: AuthErrorKind | null
  refreshAuth: () => Promise<void>
  retryInit: () => Promise<void>
  registerCaptchaContainer: (container: HTMLDivElement) => () => void
}

function classifyAuthError(error: Error): AuthErrorKind {
  const message = error.message || ''
  if (/Supabase (URL|Project) Mismatch/i.test(message)) {
    return 'config'
  }
  return 'network'
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { getCaptchaToken, captcha } = useAuthCaptcha('anonymous_signin')
  const [captchaContainer, setCaptchaContainer] = useState<HTMLDivElement | null>(null)
  const registerCaptchaContainer = useCallback((container: HTMLDivElement) => {
    setCaptchaContainer(container)
    return () => setCaptchaContainer(current => (current === container ? null : current))
  }, [])
  const [auth, setAuth] = useState<AuthState | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [errorKind, setErrorKind] = useState<AuthErrorKind | null>(null)
  const [initialized, setInitialized] = useState(false)
  const [retryNonce, setRetryNonce] = useState(0)
  const lastIdentifiedUserId = useRef<string | null>(null)
  const lastPromoSyncedUserId = useRef<string | null>(null)
  const lastLocaleSyncedUserId = useRef<string | null>(null)
  const router = useRouter()
  const pathname = usePathname() ?? '/'
  const activeLocale = useLocale()
  const authInvalidRecoveryInFlight = useRef(false)
  const initInFlightRef = useRef(false)
  const initGenerationRef = useRef(0)
  const currentAuthRef = useRef<AuthState | null>(auth)

  useEffect(() => {
    currentAuthRef.current = auth
  }, [auth])

  // Function to refresh auth state (can be called after account upgrade)
  const refreshAuth = useCallback(async () => {
    try {
      const authState = await initAuth(getCaptchaToken)
      setLibraryTransportAccount(authState?.userId ?? null, { isAnonymous: authState?.isAnonymous })
      setAuth(authState)
    } catch (err) {
      console.error('[AuthContext] Failed to refresh auth:', err)
    }
  }, [getCaptchaToken])

  // Re-run the full init flow (used by the connection-error retry button)
  const retryInit = useCallback(async () => {
    setError(null)
    setErrorKind(null)
    setIsLoading(true)
    setRetryNonce(n => n + 1)
  }, [])

  useEffect(() => {
    let isMounted = true

    async function initialize() {
      // Each init is versioned. An invalidation honored while this one is in
      // flight retires it (bumps the generation), so a late `initAuth()`
      // resolution — which may have read the now-rejected session from the
      // local copy — cannot restore that session over the sign-out.
      const generation = ++initGenerationRef.current
      const isCurrent = () => isMounted && generation === initGenerationRef.current
      initInFlightRef.current = true
      try {
        // Capture referral code from URL if present (before any async operations)
        captureReferralCode()

        // Verify frontend and backend are using the same Supabase instance BEFORE any
        // auth side effect (anonymous sign-in, profile writes) can land on the wrong one.
        // Only development can act on a mismatch (it throws and blocks the app); in
        // production the check reports to Sentry and returns, so awaiting it there
        // would put a backend round trip in front of sign-in for nothing.
        const verification = verifySupabaseUrlMatch()
        if (process.env.NODE_ENV === 'production') {
          verification.catch((err: unknown) => {
            console.error('[AuthContext] Supabase URL verification failed:', err)
            captureSentryError(err, { operation: 'supabase_url_verify_background', component: 'AuthContext' })
          })
        } else {
          await verification
        }

        const authState = await initAuth(getCaptchaToken)
        if (isCurrent()) {
          setLibraryTransportAccount(authState?.userId ?? null, { isAnonymous: authState?.isAnonymous })
          setAuth(authState)
          // A successful init is the authority on session health: drop any error
          // an earlier attempt (or a request that raced this init) left behind,
          // otherwise the connection-error screen outlives the problem.
          setError(null)
          setErrorKind(null)
          setInitialized(true)
        }
      } catch (err) {
        // A retired init may have been waiting on a challenge that was cancelled
        // by Strict Mode, unmount, or a newer init. It no longer owns auth state.
        if (!isCurrent()) return
        const error = err instanceof Error ? err : new Error(String(err))
        console.error('[AuthContext] Failed to initialize auth:', error)

        captureSentryError(error, {
          operation: 'auth_init',
          component: 'AuthContext',
          tags: {
            error_type: error.message.includes('Supabase URL mismatch') ? 'supabase_mismatch' : 'auth_init_failure',
          },
        })

        // Make Supabase URL mismatch errors very visible
        if (error.message.includes('Supabase URL mismatch') || error.message.includes('Supabase URL Mismatch')) {
          console.error(
            '\n' +
              '═══════════════════════════════════════════════════════════════\n' +
              '🚨 CRITICAL: Supabase URL Mismatch Detected!\n' +
              '═══════════════════════════════════════════════════════════════\n' +
              error.message +
              '\n' +
              '═══════════════════════════════════════════════════════════════\n',
          )
          // Show browser alert only in development for immediate visibility
          if (process.env.NODE_ENV !== 'production') {
            alert(
              '🚨 Supabase Configuration Error\n\n' +
                error.message.replace(/❌|Supabase URL Mismatch!/g, '').trim() +
                '\n\nPlease check your environment variables and restart the app.',
            )
          }
        }

        if (isCurrent()) {
          setError(error)
          setErrorKind(classifyAuthError(error))
        }
      } finally {
        // A retired init leaves the flags to whoever retired it: the
        // invalidation handler already settled loading and owns the error state.
        if (generation === initGenerationRef.current) {
          initInFlightRef.current = false
          if (isMounted) setIsLoading(false)
        }
      }
    }

    initialize()

    return () => {
      isMounted = false
    }
  }, [retryNonce, getCaptchaToken])

  // Identify user in PostHog and Sentry when auth changes
  useEffect(() => {
    if (!auth || lastIdentifiedUserId.current === auth.userId) return

    identifyUser(auth.userId, {
      alias: auth.profile.alias,
      isAnonymous: auth.isAnonymous,
      email: auth.profile.email,
    })

    // Link anonymous → authenticated identity in PostHog after signup/signin
    if (!auth.isAnonymous) {
      linkPreviousAnonymousIdentity(auth.userId)
    } else {
      // New anonymous session — clear any stale pre-reset identity
      clearPreResetIdentity()
    }

    // Set user context in Sentry for error tracking
    Sentry.setUser({
      id: auth.userId,
      email: auth.profile.email ?? undefined,
    })

    lastIdentifiedUserId.current = auth.userId
  }, [auth])

  // Library early access is email-based where the env flag is off (prod) —
  // keep the flag store in sync with the signed-in email, incl. logout.
  useEffect(() => {
    syncLibraryAccessEmail(auth?.profile.email)
  }, [auth])

  // Seed the NEXT_LOCALE cookie from the profile's saved preference on first
  // auth init — covers a fresh device or browser where the user has a saved
  // preference but no cookie yet. App surfaces then need router.refresh() so
  // the (app) layout re-resolves locale from the freshly-set cookie.
  //
  // If a cookie already exists (whether from a URL visit, LocaleSwitcher,
  // marketing middleware, or Accept-Language) we trust it — overwriting could
  // flip the UI to a stale profile value, e.g. when an updateLocalePreference
  // call failed silently after the user picked a different locale.
  //
  // The refresh re-renders the whole server tree (and re-ships the message
  // catalog) in the middle of a project boot, so it only runs when the saved
  // preference differs from the locale this document was rendered in. A new
  // anonymous user's profile is seeded from the browser locale — the same
  // signal the server already resolved — so for them the cookie is written and
  // nothing re-renders.
  useEffect(() => {
    if (!auth?.profile.locale) return
    if (lastLocaleSyncedUserId.current === auth.userId) return
    lastLocaleSyncedUserId.current = auth.userId

    if (readLocaleCookie() !== null) return

    persistLocaleChoice(auth.profile.locale)
    if (auth.profile.locale !== activeLocale && isAppRoute(stripLocalePrefix(pathname).pathname)) {
      router.refresh()
    }
  }, [auth, activeLocale, pathname, router])

  // Sync any pending promo attribution to the DB once per authenticated user.
  // Safety net for auth flows that skip /select-plan — e.g., a user visited a
  // promo landing page, closed the tab, later signed up through a different
  // route. recordPromoAttribution is an idempotent upsert.
  useEffect(() => {
    if (!auth?.userId || auth.isAnonymous) return
    if (lastPromoSyncedUserId.current === auth.userId) return
    const slug = getStoredPromoSlug()
    if (!slug) return
    lastPromoSyncedUserId.current = auth.userId
    recordPromoAttribution(slug, 'landing_page').catch(() => {})
  }, [auth])

  // Subscribe to auth state changes (e.g., after account upgrade)
  useEffect(() => {
    if (!initialized) return

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[AuthContext] Auth state changed:', event)
      // Bind/stop the durable Library outbox immediately from the auth event,
      // before the deferred profile refresh. Otherwise an old `personal`
      // queue could observe the new session token during that 100ms window.
      // `?? true` matches how initAuth and useAuth derive anonymity, so the
      // library agrees with every other real-account gate in the app — and so
      // the two binding paths can never disagree and thrash the rebind.
      setLibraryTransportAccount(session?.user.id ?? null, { isAnonymous: session?.user.is_anonymous ?? true })

      // Supabase can emit SIGNED_IN whenever it re-confirms the existing
      // session (including tab refocus). Re-reading the same profile would
      // replace the auth object and used to restart the entire project session.
      if (shouldRefreshAuthState(event, session?.user, currentAuthRef.current)) {
        // Use setTimeout to avoid blocking the auth flow and let the current operation complete.
        setTimeout(() => {
          refreshAuth()
        }, 100)
      } else if (event === 'SIGNED_OUT') {
        setAuth(null)
        resetUser() // Clear PostHog identity to prevent event misattribution
        Sentry.setUser(null) // Clear Sentry user context
        lastIdentifiedUserId.current = null
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [initialized, refreshAuth])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleAuthSessionInvalid = async (event: Event) => {
      if (authInvalidRecoveryInFlight.current) return

      const detail =
        event instanceof CustomEvent && event.detail && typeof event.detail === 'object'
          ? (event.detail as { message?: unknown; code?: unknown; endpoint?: unknown })
          : {}

      // While initialize() runs it owns the session: it creates the anonymous
      // session or recovers a stale one itself, and signing out underneath it
      // would revoke the session it is in the middle of establishing. A request
      // that found NO token in that window failed because auth was not ready
      // yet, not because a session died — so only that case is dropped. An
      // invalidation of a token the backend actually rejected is still honored,
      // whenever it arrives.
      if (initInFlightRef.current) {
        if (detail.code === 'MISSING_TOKEN') {
          console.warn('[AuthContext] Ignoring missing-token invalidation while auth is still initializing')
          return
        }
        // The backend's verdict on a token outranks whatever the pending init
        // reads from the local copy. Retire that init: a late resolution must
        // not restore the rejected session or clear the error set below. This
        // handler settles loading in its place.
        initGenerationRef.current += 1
        initInFlightRef.current = false
        setIsLoading(false)
      }
      authInvalidRecoveryInFlight.current = true

      const message =
        typeof detail.message === 'string' ? detail.message : 'Your session is no longer valid. Please sign in again.'

      const error = new Error(message)
      console.warn('[AuthContext] Auth session invalidated by API request:', {
        code: detail.code,
        endpoint: detail.endpoint,
      })

      setLibraryTransportAccount(null)
      setAuth(null)
      setError(error)
      setErrorKind('network')
      resetUser()
      Sentry.setUser(null)
      lastIdentifiedUserId.current = null

      // Local scope: the backend rejected *this* browser's token. The default
      // global scope would also delete the user's MCP-client and desktop-app
      // sessions on the server, disconnecting every assistant they connected.
      await supabase.auth.signOut({ scope: 'local' }).catch(err => {
        console.warn('[AuthContext] Failed to sign out invalid session:', err)
      })

      authInvalidRecoveryInFlight.current = false
    }

    window.addEventListener(AUTH_SESSION_INVALID_EVENT, handleAuthSessionInvalid)
    return () => window.removeEventListener(AUTH_SESSION_INVALID_EVENT, handleAuthSessionInvalid)
  }, [])

  return (
    <AuthContext.Provider
      value={{ auth, isLoading, error, errorKind, refreshAuth, retryInit, registerCaptchaContainer }}
    >
      {captcha &&
        (captchaContainer ? (
          createPortal(captcha, captchaContainer)
        ) : (
          <div className="fixed bottom-4 left-1/2 z-[100] w-[300px] -translate-x-1/2">{captcha}</div>
        ))}
      {children}
    </AuthContext.Provider>
  )
}

/** Keep bootstrap verification inside an auth dialog's focus and pointer scope. */
export function AuthCaptchaContainer() {
  const context = useContext(AuthContext)
  return <div ref={context?.registerCaptchaContainer} className="empty:hidden" />
}

export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}

/**
 * The auth context when there is one, null when there is not. For gates that
 * only ever widen what a signed-in person sees: outside an `AuthProvider`
 * (an isolated panel render, a test) the honest answer is "not this person",
 * not a thrown render.
 */
export function useOptionalAuthContext(): AuthContextValue | null {
  return useContext(AuthContext)
}
